// Session context: single source of truth for "am I signed in?" across the
// app. On boot we call /v1/auth/me; cookies are sent automatically via the
// fetch wrapper. Three states:
//   loading -> we don't know yet (don't redirect)
//   anon    -> no session
//   signed  -> { user, workspaces, currentWorkspaceId }
//
// Pages that need auth use `useSession()` and read .state. The /app route
// has a beforeLoad guard that redirects when state===anon (see app/route.tsx).

import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { ApiError, auth, type MeResponse } from "./api";
import { useI18n } from "./i18n";

type SessionState =
  | { state: "loading" }
  | { state: "anon" }
  | { state: "signed"; me: MeResponse };

type SessionContextValue = SessionState & {
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
};

const SessionContext = createContext<SessionContextValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [s, setS] = useState<SessionState>({ state: "loading" });
  // Note: useI18n() returns a stable callback identity from the parent
  // provider, so reading it once here would be fine — but doing it inside
  // refresh() keeps refresh() referentially stable across re-renders.
  const { adoptServerLocale } = useI18n();

  const refresh = useCallback(async () => {
    try {
      const me = await auth.me();
      // Server's stored locale is the authoritative source for signed-in
      // users — propagate so the UI matches what notif-service will send
      // in emails. adoptServerLocale is a no-op if the user has clicked
      // the locale toggle already (their pick wins).
      adoptServerLocale(me.user.locale);
      setS({ state: "signed", me });
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) {
        setS({ state: "anon" });
      } else {
        // Network or 5xx — treat as anonymous so the user can at least see
        // the login screen instead of an infinite spinner.
        setS({ state: "anon" });
      }
    }
  }, [adoptServerLocale]);

  const signOut = useCallback(async () => {
    try {
      await auth.logout();
    } catch {
      // Even if the server-side revoke fails, drop client state.
    }
    setS({ state: "anon" });
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const value = useMemo<SessionContextValue>(() => ({ ...s, refresh, signOut }), [s, refresh, signOut]);
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error("useSession must be used inside <SessionProvider>");
  return v;
}
